import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, Trash2, Pencil, Save, X, Loader2, StickyNote, History } from "lucide-react";
import { API } from "@/lib/auth";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { BRIDAL_STATUS, STATUS_KEYS, StatusBadge, prettyDate, countdownLabel } from "./bridalStatus";
import BridalDepositPanel from "./BridalDepositPanel";
import BridalBalancePanel from "./BridalBalancePanel";

const DETAIL_FIELDS = [
  ["full_name", "Full name"], ["phone", "Mobile"], ["wedding_date", "Wedding date", "date"],
  ["wedding_venue", "Wedding venue"], ["ceremony_time", "Ceremony time", "time"],
  ["departure_time", "Departure time", "time"], ["getting_ready_location", "Getting-ready location"],
  ["service_location", "Mobile / salon", "select", ["", "mobile", "salon", "unsure"]],
  ["people_count", "People requiring hair", "number"],
  ["who_requires_hair", "Who requires hair", "list"], ["services_required", "Services required", "list"],
  ["wears_extensions", "Currently wears extensions", "select", ["", "yes", "no"]],
  ["extensions_interest", "Interested in extensions", "select", ["", "yes", "no", "unsure"]],
  ["about_wedding", "About the wedding", "textarea"],
];
const BOOKING_FIELDS = [
  ["trial_date", "Trial date", "date"], ["quote_amount", "Quote total (£)", "number"],
  ["deposit_amount", "Deposit (£)", "number"], ["deposit_paid", "Deposit paid", "bool"],
  ["balance_amount", "Balance (£)", "number"], ["balance_paid", "Balance paid", "bool"],
];
const LOC = { mobile: "Mobile — at getting-ready location", salon: "In salon", unsure: "Not sure yet" };

const fmt = (k, v) => {
  if (v === null || v === undefined || v === "" || (Array.isArray(v) && !v.length)) return "—";
  if (Array.isArray(v)) return v.join(", ");
  if (k === "wedding_date" || k === "trial_date") return prettyDate(v);
  if (k === "service_location") return LOC[v] || v;
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return String(v);
};

const inputCls = "w-full rounded-xl border border-[hsl(var(--onyx)/0.15)] bg-white px-3 py-2 text-sm";

function FieldInput({ def, value, onChange }) {
  const [k, , type, opts] = def;
  const tid = `bridal-edit-${k}`;
  if (type === "textarea") return <textarea data-testid={tid} rows={3} className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} />;
  if (type === "select") return (
    <select data-testid={tid} className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value || null)}>
      {opts.map((o) => <option key={o} value={o}>{o ? (LOC[o] || o) : "—"}</option>)}
    </select>
  );
  if (type === "bool") return (
    <label className="inline-flex items-center gap-2 text-sm">
      <input data-testid={tid} type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-[#8B1B3A]" /> Paid
    </label>
  );
  if (type === "list") return <input data-testid={tid} className={inputCls} value={(value || []).join(", ")} onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} placeholder="Comma separated" />;
  return <input data-testid={tid} type={type || "text"} className={inputCls} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />;
}

function EditableGrid({ title, defs, enquiry, onSave, testId, startEditing = false }) {
  const [editing, setEditing] = useState(startEditing);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { setEditing(false); setDraft({}); }, [enquiry.id]);
  const start = () => { setDraft(Object.fromEntries(defs.map(([k]) => [k, enquiry[k] ?? null]))); setEditing(true); };
  const save = async () => {
    setSaving(true);
    const changed = Object.fromEntries(Object.entries(draft).filter(([k, v]) => JSON.stringify(v) !== JSON.stringify(enquiry[k] ?? null)));
    if (Object.keys(changed).length) await onSave({ fields: changed });
    setSaving(false); setEditing(false);
  };
  return (
    <section data-testid={testId} className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="overline">{title}</div>
        {editing ? (
          <div className="flex gap-2">
            <button onClick={() => setEditing(false)} data-testid={`${testId}-cancel`} className="text-xs px-3 py-1.5 rounded-full border"><X size={12} className="inline" /> Cancel</button>
            <button onClick={save} disabled={saving} data-testid={`${testId}-save`} className="text-xs px-3 py-1.5 rounded-full bg-[hsl(var(--onyx))] text-[hsl(var(--cream))]">
              {saving ? <Loader2 size={12} className="inline animate-spin" /> : <Save size={12} className="inline" />} Save
            </button>
          </div>
        ) : (
          <button onClick={start} data-testid={`${testId}-edit`} className="text-xs px-3 py-1.5 rounded-full border inline-flex items-center gap-1"><Pencil size={12} /> Edit</button>
        )}
      </div>
      <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
        {defs.map((def) => (
          <div key={def[0]} className={def[2] === "textarea" ? "sm:col-span-2" : ""}>
            <dt className="text-[10px] uppercase tracking-[0.16em] text-[hsl(var(--onyx)/0.5)]">{def[1]}</dt>
            <dd className="mt-1 text-sm whitespace-pre-line break-words" data-testid={`bridal-detail-${def[0]}`}>
              {editing ? <FieldInput def={def} value={draft[def[0]]} onChange={(v) => setDraft((d) => ({ ...d, [def[0]]: v }))} /> : fmt(def[0], enquiry[def[0]])}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function Notes({ enquiry, onChange }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/bridal/enquiries/${enquiry.id}/notes`, { text });
      onChange(r.data); setText(""); toast.success("Note added");
    } catch { toast.error("Could not add note"); } finally { setBusy(false); }
  };
  const del = async (nid) => {
    if (!window.confirm("Delete this note?")) return;
    const r = await axios.delete(`${API}/admin/bridal/enquiries/${enquiry.id}/notes/${nid}`);
    onChange(r.data);
  };
  return (
    <section data-testid="bridal-notes" className="rounded-3xl bg-[#FFF9EC] border border-[#E9D9AE] p-5">
      <div className="overline flex items-center gap-2"><StickyNote size={12} /> Private notes · only you can see these</div>
      <textarea data-testid="bridal-note-input" rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="e.g. Called 12 Mar — sending quote for 4 people + trial"
        className="mt-3 w-full rounded-xl border border-[#E9D9AE] bg-white px-3 py-2 text-sm" />
      <button onClick={add} disabled={busy || !text.trim()} data-testid="bridal-note-add-btn" className="mt-2 text-xs px-4 py-2 rounded-full bg-[hsl(var(--onyx))] text-[hsl(var(--cream))] disabled:opacity-50">
        {busy ? "Adding…" : "Add note"}
      </button>
      <ul className="mt-4 space-y-3">
        {[...(enquiry.admin_notes || [])].reverse().map((n) => (
          <li key={n.id} data-testid={`bridal-note-${n.id}`} className="rounded-2xl bg-white p-3 text-sm">
            <div className="flex justify-between gap-3">
              <p className="whitespace-pre-line break-words">{n.text}</p>
              <button onClick={() => del(n.id)} aria-label="Delete note" data-testid={`bridal-note-delete-${n.id}`} className="text-[hsl(var(--onyx)/0.4)] hover:text-red-600 shrink-0"><Trash2 size={13} /></button>
            </div>
            <div className="mt-1 text-[10px] text-[hsl(var(--onyx)/0.45)]">{new Date(n.at).toLocaleString("en-GB")}</div>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function BridalEnquiryDetail({ enquiry, onClose, onChange, onDeleted }) {
  const patch = async (body) => {
    try {
      const r = await axios.patch(`${API}/admin/bridal/enquiries/${enquiry.id}`, body);
      onChange(r.data); toast.success("Enquiry updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  const del = async () => {
    if (!window.confirm(`Delete ${enquiry.full_name}'s enquiry permanently?`)) return;
    await axios.delete(`${API}/admin/bridal/enquiries/${enquiry.id}`);
    toast.success("Enquiry deleted"); onDeleted();
  };
  return (
    <Sheet open={!!enquiry} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" data-testid="bridal-enquiry-detail" className="w-full sm:max-w-2xl overflow-y-auto bg-[hsl(var(--cream))] p-4 sm:p-6">
        {enquiry && (
          <div className="space-y-4 pb-10">
            <SheetHeader className="text-left">
              <SheetTitle className="font-display text-3xl font-normal">{enquiry.full_name}</SheetTitle>
              <SheetDescription asChild>
                <div className="text-sm">
                  <div data-testid="bridal-detail-wedding-headline" className="text-[hsl(var(--onyx))] font-medium">
                    {prettyDate(enquiry.wedding_date, { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                    <span className="ml-2 text-[#8B1B3A]">{countdownLabel(enquiry.wedding_date)}</span>
                  </div>
                  <div className="mt-1 text-xs text-[hsl(var(--onyx)/0.55)]">Ref {enquiry.reference} · received {new Date(enquiry.created_at).toLocaleDateString("en-GB")}</div>
                </div>
              </SheetDescription>
            </SheetHeader>

            <section className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="overline">Status</div>
                <StatusBadge status={enquiry.status} testId="bridal-detail-status-badge" />
              </div>
              <select data-testid="bridal-status-select" value={enquiry.status} onChange={(e) => patch({ status: e.target.value })}
                className="mt-3 w-full rounded-xl border border-[hsl(var(--onyx)/0.15)] bg-white px-3 py-2.5 text-sm">
                {STATUS_KEYS.map((k) => <option key={k} value={k}>{BRIDAL_STATUS[k].l}</option>)}
              </select>
              <div className="mt-4 flex flex-wrap gap-2">
                <a href={`mailto:${enquiry.email}?subject=Your bridal enquiry (${enquiry.reference})`} data-testid="bridal-detail-email-link" className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-full border break-all"><Mail size={12} /> {enquiry.email}</a>
                <a href={`tel:${enquiry.phone}`} data-testid="bridal-detail-phone-link" className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-full border"><Phone size={12} /> {enquiry.phone}</a>
              </div>
            </section>

            <BridalDepositPanel enquiry={enquiry} onChange={onChange} />
            <BridalBalancePanel enquiry={enquiry} onChange={onChange} />
            <EditableGrid title="Booking progress" defs={BOOKING_FIELDS} enquiry={enquiry} onSave={patch} testId="bridal-booking-section" />
            <EditableGrid title="Wedding & enquiry details" defs={DETAIL_FIELDS} enquiry={enquiry} onSave={patch} testId="bridal-details-section" />

            <section data-testid="bridal-detail-photos" className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-5">
              <div className="overline mb-3">Inspiration photos ({enquiry.inspiration_images?.length || 0})</div>
              {enquiry.inspiration_images?.length ? (
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                  {enquiry.inspiration_images.map((img, i) => (
                    <a key={img.url} href={img.url} target="_blank" rel="noreferrer" data-testid={`bridal-detail-photo-${i}`} className="block aspect-square rounded-xl overflow-hidden bg-[hsl(var(--blush)/0.4)]">
                      <img src={img.url} alt={`Inspiration ${i + 1}`} className="w-full h-full object-cover" />
                    </a>
                  ))}
                </div>
              ) : <p className="text-sm text-[hsl(var(--onyx)/0.5)]">No photos uploaded.</p>}
            </section>

            <Notes enquiry={enquiry} onChange={onChange} />

            <section data-testid="bridal-history" className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-5">
              <div className="overline flex items-center gap-2 mb-3"><History size={12} /> Activity</div>
              <ul className="space-y-2">
                {[...(enquiry.history || [])].reverse().map((h, i) => (
                  <li key={i} className="text-xs flex flex-wrap gap-x-2">
                    <span className="text-[hsl(var(--onyx)/0.45)]">{new Date(h.at).toLocaleString("en-GB")}</span>
                    <span>{h.event}{h.status ? ` → ${BRIDAL_STATUS[h.status]?.l || h.status}` : ""}</span>
                  </li>
                ))}
              </ul>
              {enquiry.emails && (
                <p className="mt-3 text-[11px] text-[hsl(var(--onyx)/0.5)]" data-testid="bridal-detail-email-status">
                  Business notification: {enquiry.emails.admin_id ? "sent" : "not sent"} · Thank-you email to bride: {enquiry.emails.ack_id ? "sent" : "not sent"}
                </p>
              )}
            </section>

            <button onClick={del} data-testid="bridal-delete-enquiry-btn" className="text-xs text-red-600 inline-flex items-center gap-1.5 px-3 py-2"><Trash2 size={12} /> Delete enquiry</button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
